import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit, startAfter, orderBy, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import { useBlogCache } from '../../context/BlogCacheContext';
import MetaTags from '../common/MetaTags';

const POSTS_PER_PAGE = 10;

export default function BlogView() {
  // Read category from URL on initial load
  const urlParams = new URLSearchParams(window.location.search);
  const categoryFromUrl = urlParams.get('category');

  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [countCache, setCountCache] = useState({});
  const [lastVisibleDocs, setLastVisibleDocs] = useState({}); // Store cursor for each page
  const {
    categories,
    getCachedPosts,
    setCachedPosts,
    getCachedCount,
    setCachedCount
  } = useBlogCache();

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when category changes
    setLastVisibleDocs({}); // Clear cursors when category changes
    loadPosts(1, true); // true = need to fetch count
  }, [selectedCategory]);

  useEffect(() => {
    loadPosts(currentPage, false); // false = don't need to refetch count
  }, [currentPage]);

  async function loadPosts(page = 1, needCount = false) {
    try {
      const cacheKey = selectedCategory || 'all';

      // Check cache first
      const cachedPosts = getCachedPosts(page, selectedCategory);
      if (cachedPosts) {
        setPosts(cachedPosts);
        setLoading(false);

        // Check cached count
        const cachedCount = getCachedCount(selectedCategory);
        if (cachedCount !== null) {
          setTotalPosts(cachedCount);
        }
        return;
      }

      setLoading(true);

      // Build base query
      let baseQuery;
      if (selectedCategory) {
        baseQuery = [
          collection(db, 'blog_posts'),
          where('published', '==', true),
          where('category', '==', selectedCategory),
          orderBy('createdAt', 'desc')
        ];
      } else {
        baseQuery = [
          collection(db, 'blog_posts'),
          where('published', '==', true),
          orderBy('createdAt', 'desc')
        ];
      }

      // Add cursor for pagination (if not first page)
      let postsQuery;
      if (page > 1 && lastVisibleDocs[page - 1]) {
        postsQuery = query(
          ...baseQuery,
          startAfter(lastVisibleDocs[page - 1]),
          limit(POSTS_PER_PAGE)
        );
      } else {
        postsQuery = query(...baseQuery, limit(POSTS_PER_PAGE));
      }

      // Fetch posts
      const postsSnapshot = await getDocs(postsQuery);
      const loadedPosts = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Store last visible document for next page
      if (postsSnapshot.docs.length > 0) {
        setLastVisibleDocs(prev => ({
          ...prev,
          [page]: postsSnapshot.docs[postsSnapshot.docs.length - 1]
        }));
      }

      // Cache the posts
      setCachedPosts(page, selectedCategory, loadedPosts);

      // Set posts immediately (don't wait for count)
      setPosts(loadedPosts);
      setLoading(false);

      // Load count in parallel (non-blocking)
      if (needCount || !countCache[cacheKey]) {
        let countQuery;
        if (selectedCategory) {
          countQuery = query(
            collection(db, 'blog_posts'),
            where('published', '==', true),
            where('category', '==', selectedCategory)
          );
        } else {
          countQuery = query(
            collection(db, 'blog_posts'),
            where('published', '==', true)
          );
        }

        // Load count without blocking (happens after posts are shown)
        getCountFromServer(countQuery).then(countSnapshot => {
          const count = countSnapshot.data().count;
          setTotalPosts(count);
          setCountCache(prev => ({ ...prev, [cacheKey]: count }));
          // Also cache in context
          setCachedCount(selectedCategory, count);
        }).catch(err => {
          console.error('Error fetching count:', err);
        });
      } else {
        // Use cached count
        const cachedCount = getCachedCount(selectedCategory);
        if (cachedCount !== null) {
          setTotalPosts(cachedCount);
        } else {
          setTotalPosts(countCache[cacheKey]);
        }
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      console.error('Error details:', error.code, error.message);
      setLoading(false);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  return (
    <BlogLayout>
      <MetaTags
        title="Blog"
        description="Tips, guides, and insights for volleyball players and coaches. Learn how to improve your game with data-driven volleyball training and performance tracking."
        type="website"
      />
      <style>{`
        .blog-view-header {
          margin-bottom: 40px;
        }

        .blog-view-title {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 12px;
          color: var(--primary);
          line-height: 1.2;
        }

        .blog-view-subtitle {
          font-size: 18px;
          color: var(--muted);
          line-height: 1.6;
        }

        .blog-post-card {
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #e5e5e5;
          overflow: hidden;
        }

        .blog-post-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }

        .blog-post-image {
          width: 100%;
          aspect-ratio: 1200 / 630;
          background-size: cover;
          background-position: center;
          margin-bottom: 16px;
        }

        .blog-post-content {
          padding: 16px;
        }

        .blog-post-content-with-image {
          padding: 0 16px 16px;
        }

        .blog-post-title {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #1a1a1a;
          line-height: 1.3;
        }

        @media (max-width: 768px) {
          .blog-view-title {
            font-size: 32px;
          }

          .blog-view-subtitle {
            font-size: 16px;
          }

          .blog-view-header {
            margin-bottom: 24px;
          }

          .blog-post-image {
            aspect-ratio: 1200 / 630;
          }

          .blog-post-title {
            font-size: 20px;
          }
        }

        @media (max-width: 480px) {
          .blog-view-title {
            font-size: 28px;
          }

          .blog-view-subtitle {
            font-size: 15px;
          }

          .blog-post-image {
            aspect-ratio: 1200 / 630;
          }

          .blog-post-title {
            font-size: 18px;
          }
        }
      `}</style>

      {/* Header */}
      <header className="blog-view-header">
        <h1 className="blog-view-title">
          Blog
        </h1>
        <p className="blog-view-subtitle">
          Tips, guides, and insights for volleyball players and coaches
        </p>
      </header>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setSelectedCategory(null)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: selectedCategory === null ? '2px solid var(--primary)' : '1px solid #ddd',
              background: selectedCategory === null ? 'var(--primary)' : 'white',
              color: selectedCategory === null ? 'white' : '#666',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            All Posts
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.slug)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: selectedCategory === cat.slug ? '2px solid var(--primary)' : '1px solid #ddd',
                background: selectedCategory === cat.slug ? 'var(--primary)' : 'white',
                color: selectedCategory === cat.slug ? 'white' : '#666',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
            Loading posts...
          </div>
        </div>
      )}

      {/* Posts Grid */}
      {!loading && posts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: 'var(--muted)', marginBottom: '8px' }}>
            {selectedCategory ? 'No posts in this category' : 'No posts yet'}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
            {selectedCategory ? 'Try selecting a different category or view all posts.' : 'Check back soon for new content!'}
          </div>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {posts.map(post => (
              <article
                key={post.id}
                onClick={() => window.location.href = `/blog/${post.slug}`}
                className="card blog-post-card"
              >
                {/* Featured Image */}
                {post.featuredImage && (
                  <div className="blog-post-image">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className={post.featuredImage ? 'blog-post-content-with-image' : 'blog-post-content'}>
                  {/* Category Badge */}
                  {post.categoryName && (
                    <div style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      background: 'rgba(46, 90, 172, 0.1)',
                      color: 'var(--primary)',
                      fontSize: '12px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {post.categoryName}
                    </div>
                  )}

                  <h2 className="blog-post-title">
                    {post.title}
                  </h2>

                  {post.excerpt && (
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--muted)',
                      lineHeight: '1.6',
                      marginBottom: '12px'
                    }}>
                      {post.excerpt}
                    </p>
                  )}

                  <div style={{
                    fontSize: '13px',
                    color: 'var(--muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span>{formatDate(post.createdAt)}</span>
                    {post.readTime && (
                      <>
                        <span>•</span>
                        <span>{post.readTime} min read</span>
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPosts > POSTS_PER_PAGE && (
            <div style={{
              marginTop: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: currentPage === 1 ? '#f5f5f5' : 'white',
                  color: currentPage === 1 ? '#999' : 'var(--text)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Previous
              </button>

              <div style={{
                fontSize: '14px',
                color: 'var(--muted)',
                padding: '0 16px'
              }}>
                Page {currentPage} of {Math.ceil(totalPosts / POSTS_PER_PAGE)}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalPosts / POSTS_PER_PAGE), p + 1))}
                disabled={currentPage === Math.ceil(totalPosts / POSTS_PER_PAGE)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: currentPage === Math.ceil(totalPosts / POSTS_PER_PAGE) ? '#f5f5f5' : 'white',
                  color: currentPage === Math.ceil(totalPosts / POSTS_PER_PAGE) ? '#999' : 'var(--text)',
                  cursor: currentPage === Math.ceil(totalPosts / POSTS_PER_PAGE) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </BlogLayout>
  );
}
